import { Factory } from "@/data/dao/Factory";
import { SystemDao } from "@/data/dao/service/SystemDao";
import { TaskExecutionState } from "@/data/model/Enums";
import type { IRecurringTask } from "@/data/model/RecurringTask";

import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { UiNotification } from "@/portal/ui/Notification";
import { PortalDateTime } from "@/portal/util/PortalDateTime";

export class TaskTableController {
    private $element: JQuery;
    private dao: SystemDao;

    private dataTable: DataTable<IRecurringTask>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<SystemDao>(SystemDao);

        const tableConfig: IDataTableConfig<IRecurringTask> = {
            columnMapping: [
                {
                    name: i18n("general.fields.name"),
                    property: "name",
                    styles: { width: "32%" },
                },
                {
                    name: i18n("general.fields.active"),
                    property: "isActive",
                    styles: { width: "15%" },
                    formatter: (m, p) => (p ? i18n("general.yes") : i18n("general.no")),
                },
                {
                    name: i18n("task.fields.interval"),
                    property: "interval",
                    styles: { width: "15%" },
                },
                {
                    name: i18n("general.fields.state"),
                    property: "state",
                    sortable: false,
                    styles: { width: "15%" },
                    formatter: (m, p) => {
                        let styleClass = "kt-badge--warning";
                        switch (p) {
                            case TaskExecutionState.Executing:
                                styleClass = "kt-badge--success";
                                break;
                            case TaskExecutionState.Scheduled:
                                styleClass = "kt-badge--primary";
                                break;
                        }

                        return (
                            '<div class="kt-badge ' +
                            styleClass +
                            ' kt-badge--inline">' +
                            i18n("enums.TaskExecutionState." + TaskExecutionState[p]) +
                            "</div>"
                        );
                    },
                },
                {
                    name: i18n("task.fields.nextExecution"),
                    property: "nextExecution",
                    sortable: false,
                    formatter: (m, p) => (p === undefined ? "" : PortalDateTime.toRelativeString(p)),
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "55px", "text-align": "right" },
                    formatter: (m, p) => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-play-circle-o"></i></a></span>'
                        )
                            .data("id", p)
                            .click((e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                this.executeTask($(e.currentTarget).data("id"));
                            });
                    },
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "55px", "text-align": "right" },
                    formatter: () => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
            defaultSortAsc: true,
            defaultSortProperty: "name",
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.dataTable = new DataTable<IRecurringTask>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao.taskDataSource,
            tableConfig
        );
        this.dataTable.onClick(
            (m) => {
                location.href = "/task/" + m.id;
            },
            (m) => {
                window.open("/task/" + m.id);
            }
        );

        this.dataTable.listenTo(this.search);
    }

    private executeTask(id: string) {
        this.dao
            .executeRecurringTask(id)
            .then(() => {
                UiNotification.showSuccess(i18n("task.started"));
                return this.dataTable.loadData();
            })
            .catch((ex) => {
                logError(ex);
                UiNotification.showException(i18n("task.failedStart"));
            });
    }
}
